import { useNavigate, useParams } from "react-router-dom";
import { ApiOperationItems } from "~enums/apiOperationItems";
import { useApiOperations } from "~hooks/useApiOperations.ts";
import { useExportEntityGroups } from "~pages/EntityGroups/EntityGroupListPage/hooks/useExportEntityGroups.ts";
import { useImportEntityGroups } from "~pages/EntityGroups/EntityGroupListPage/hooks/useImportEntityGroups.ts";
import type { CreateEntityGroup, UpdateEntityGroup } from "~pages/EntityGroups/types/upsertEntityGroup.ts";
import type { DefaultOperationSuccessResponse } from "~types/response.ts";
import { isDefaultOperationSucceeded } from "~utils/defaultOperationResponse.ts";
import { useCreateEntityGroup } from "./useCreateEntityGroup.ts";
import { useDeleteEntityGroup } from "./useDeleteEntityGroup.ts";
import { useUpdateEntityGroup } from "./useUpdateEntityGroup.ts";

export const useEntityGroupService = () => {
  const { mutateAsync: createEntityGroupFn, isPending: isCreatePending } = useCreateEntityGroup();
  const { mutateAsync: updateEntityGroupFn, isPending: isUpdatePending } = useUpdateEntityGroup();
  const { mutateAsync: deleteEntityGroupFn, isPending: isDeletePending } = useDeleteEntityGroup();
  const { mutateAsync: importEntityGroupsFn, isPending: isImportPending } = useImportEntityGroups();
  const { mutateAsync: exportEntityGroupsFn, isPending: isExportPending } = useExportEntityGroups();
  const { customerId, sport, organizationId } = useParams();
  const navigate = useNavigate();

  const { create, update, remove, executeOrThrow } = useApiOperations(ApiOperationItems.ENTITY_GROUP);

  const createEntityGroup = async (payload: CreateEntityGroup) => {
    const createdEntityGroup = await create(() => createEntityGroupFn(payload));

    if (createdEntityGroup) {
      navigate(`/${customerId}/admin/${sport}/${organizationId}/entity-groups`, { state: { search: payload.nameFullLocal } });
    }

    return createdEntityGroup;
  };

  const updateEntityGroup = (entityGroupId: string, payload: Omit<UpdateEntityGroup, "entityGroupId">) =>
    update(() => updateEntityGroupFn({ ...payload, entityGroupId }));

  const deleteEntityGroup = async (entityGroupId: string) => Boolean(await remove(() => deleteEntityGroupFn(entityGroupId)));

  const importEntityGroups = async (fileName: string) =>
    executeOrThrow(() => importEntityGroupsFn(fileName), {
      successMessageId: "entityGroups.import.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  const exportEntityGroups = async (fieldNames: Array<string>): Promise<DefaultOperationSuccessResponse> =>
    executeOrThrow(() => exportEntityGroupsFn(fieldNames), {
      successMessageId: "entityGroups.export.started",
      isSucceeded: isDefaultOperationSucceeded,
    });

  return {
    createEntityGroup,
    isCreatePending,
    updateEntityGroup,
    isUpdatePending,
    deleteEntityGroup,
    isDeletePending,
    importEntityGroups,
    isImportPending,
    exportEntityGroups,
    isExportPending,
  };
};
